import React, {useContext, useMemo, useState} from 'react'
import AppContext from '../../appContext'
import LimeProgramLabInputs from '../../controllers/LimeProgramLabInputs'
import LimePlayFieldRow from '../../controllers/LimePlayFieldRow'
import LabInputUnit from '../../controllers/LabInputUnit'
import SampleDepth from '../../controllers/SampleDepth'
import NullableMeasureValue from '../../controllers/NullableMeasureValue'
import CheckBox from '../../components/CheckBox'
import Dialog from '../../components/Dialog'
import FileUploader from '../../components/FileUploader'
import SampleLocationsDialog from './SampleLocationsDialog'
import {PlusCircleIcon, ArrowUpOnSquareIcon, MapIcon, FunnelIcon, ChevronUpIcon, ChevronDownIcon} from '@heroicons/react/24/solid'
import {XMarkIcon} from '@heroicons/react/24/outline'
import {arrayPush, arrayRemoveIndex, arrayUpdate} from '../../immutableState'
import {parseCSVFile} from '../program/CSVParse'
import {min} from '../../wrapper'
import {customUnits, unitCustom} from '../program/CustomUnits'
import {NullableMeasureValueField} from '../program/MeasureValueField'
import NumberNullable from '../../components/NumberNullable'
import Number from '../../components/Number'
import {onInputHandler} from '../../wrapper'
import {KeyOfType} from '../../components/Fields'
import {calcElementalSulphurNeed} from './limeCalcs'
import {
    RowWithId,
    toRowsWithId,
    toLabInputs,
    createEmptyRow,
    isMeq100g,
    formatValue,
    formatMeasureElement,
    formatMgKg,
    formatSatPercent,
    convertRowForEditing,
    convertRowForSaving,
    buildLimeSoilColumnMatchers,
    rowsToSamplePoints
} from './limeSoilUtils'
import {getValueNullable} from '../program/Helpers'
import MeasurementUnit from '../../controllers/MeasurementUnit'

// Column group keys for visibility toggles
type ColumnGroupKey = 'soilData' | 'recommendations' | 'nutrientLevels'

// Sortable columns — defined once, types derived with `as const`
const SOIL_SORT_COLUMNS = [
    'landName', 'referenceNr', 'sampleDepth',
    'phH2O', 'pBrayI', 'k', 'na', 'ca', 'mg', 'exchH', 'caPercent', 'mgPercent',
    'kPercent', 'naPercent', 'acidSat', 'caMgRatio', 'caMgKRatio', 'mgKRatio',
    'sValue', 'naKRatio', 'cec', 'density'
] as const

const CALC_SORT_COLUMNS = [
    'limeType',
    'amendmentNeedKgHa', 'appliedYear1KgHa', 'appliedYear2KgHa',
    'gypsumNeedKgHa', 'gypsumYear1KgHa',
    'kNeedKgHa', 'kFertYear1KgHa',
    'pNeedKgHa', 'pFertYear1KgHa',
    'elementalSulphurKgHa',
    'newCaMgKg', 'newMgMgKg', 'newKMgKg', 'newPMgKg', 'newNaMgKg',
    'newCaMgKgY2', 'newMgMgKgY2', 'newKMgKgY2', 'newPMgKgY2', 'newNaMgKgY2',
    'satAfterCa', 'satAfterMg', 'satAfterK', 'satAfterNa',
    'satAfterCaY2', 'satAfterMgY2', 'satAfterKY2', 'satAfterNaY2'
] as const

type SoilSortColumn = typeof SOIL_SORT_COLUMNS[number]
type CalcSortColumn = typeof CALC_SORT_COLUMNS[number]
type SortableColumn = SoilSortColumn | CalcSortColumn
type SortDirection = 'asc' | 'desc' | null

const SOIL_SORT_SET = new Set<string>(SOIL_SORT_COLUMNS)

function isSoilColumn(col: SortableColumn): col is SoilSortColumn {
    return SOIL_SORT_SET.has(col)
}

// String sort columns use localeCompare instead of numeric subtraction
const STRING_SORT_COLUMNS: Set<string> = new Set(['landName', 'referenceNr'])

function getSoilSortValue(row: RowWithId, column: SoilSortColumn, system: MeasurementUnit): number {
    switch (column) {
        case 'sampleDepth':
            return row.sampleDepth === SampleDepth.TopSoil ? 0 : row.sampleDepth === SampleDepth.SubSoil ? 1 : 2
        case 'pBrayI': case 'k': case 'na': case 'ca': case 'mg':
            return getValueNullable(system, row[column]) ?? 0
        case 'phH2O': case 'exchH': case 'density':
            return row[column] || 0
        default:
            return (row[column] as number | null) ?? 0
    }
}

function getCalcSortValue(calc: LimePlayFieldRow | undefined, column: CalcSortColumn): number {
    if (!calc) return 0
    if (column === 'limeType') return calc.limeType === 'Dolomitic' ? 1 : 0
    return calc[column] ?? 0
}
// Sort indicator component - always shows both arrows, highlights active direction
const SortIndicator: React.FC<{column: SortableColumn, sortColumn: SortableColumn | null, sortDirection: SortDirection}> = ({column, sortColumn, sortDirection}) => {
    const isActive = sortColumn === column
    const isAsc = isActive && sortDirection === 'asc'
    const isDesc = isActive && sortDirection === 'desc'

    return (
        <span className="inline-flex flex-col ml-1 -my-1">
            <ChevronUpIcon
                className="w-3 h-3 -mb-1"
                style={{color: isAsc ? '#8ABE3C' : '#D1D5DB'}}
            />
            <ChevronDownIcon
                className="w-3 h-3"
                style={{color: isDesc ? '#8ABE3C' : '#D1D5DB'}}
            />
        </span>
    )
}

// Shared table styling constants (matches LimeProgram.tsx patterns)
const tableStyles = {
    headerGroup: 'whitespace-nowrap px-2 py-2 text-center text-xs font-semibold bg-gray-200 border-l-2 border-gray-100',
    headerSub: 'whitespace-nowrap px-2 py-1 text-xs font-semibold bg-gray-200 border-l-2 border-gray-100',
    headerSubAlt: 'whitespace-nowrap px-2 py-1 text-xs font-semibold bg-gray-100 border-l-2 border-gray-100',
    cell: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100',
    cellRight: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100 text-right',
    cellRightBold: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100 text-right font-semibold',
    cellOverride: 'whitespace-nowrap px-1 py-1 text-xs border-l-2 border-b-2 border-gray-100 bg-gray-50',
    input: 'w-16 text-xs p-1 border border-gray-200 rounded text-right',
    select: 'w-full text-xs p-1 border border-gray-200 rounded bg-white',
    stickyHeader: 'whitespace-nowrap px-2 py-2 text-left text-xs font-semibold bg-gray-200 sticky z-20',
    stickyCell: 'whitespace-nowrap px-2 py-2 text-xs text-gray-600 border-l-2 border-b-2 border-gray-100 sticky z-10 bg-white'
}

// Override number input component with optional unit conversion
const OverrideInput: React.FC<{
    value: number | null | undefined
    onChange: (value: number | null) => void
    toDisplay?: (v: number) => number
    toStorage?: (v: number) => number
}> = ({value, onChange, toDisplay, toStorage}) => {
    const displayValue = value != null && toDisplay
        ? Math.round(toDisplay(value) * 100) / 100
        : value
    return (
        <input
            type="number"
            className={tableStyles.input}
            value={displayValue ?? ''}
            onChange={e => {
                if (e.target.value === '') {
                    onChange(null)
                } else {
                    const parsed = parseFloat(e.target.value)
                    onChange(toStorage ? toStorage(parsed) : parsed)
                }
            }}
            onWheel={e => e.currentTarget.blur()}
            placeholder="-"
        />
    )
}

// Bulk override input for header - applies on Enter key
const BulkOverrideInput: React.FC<{
    type: 'number' | 'select'
    options?: {value: string, label: string}[]
    hasSelection: boolean
    onApply: (value: number | string | null) => void
}> = ({type, options, hasSelection, onApply}) => {
    const [localValue, setLocalValue] = useState<string>('')

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && localValue !== '' && hasSelection) {
            onApply(type === 'number' ? parseFloat(localValue) : localValue)
        }
    }

    const handleSelectChange = (value: string) => {
        setLocalValue(value)
        if (value !== '' && hasSelection) {
            onApply(value)
        }
    }

    const inputClass = "w-16 text-xs p-1 border border-gray-300 rounded bg-white font-normal [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"

    if (type === 'select' && options) {
        return (
            <select
                className={`${inputClass} w-20`}
                value={localValue}
                onChange={e => handleSelectChange(e.target.value)}
                disabled={!hasSelection}
            >
                <option value="">-</option>
                {options.map(opt => (
                    <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
            </select>
        )
    }

    return (
        <input
            type="number"
            className={inputClass}
            value={localValue}
            onChange={e => setLocalValue(e.target.value)}
            onKeyDown={handleKeyDown}
            onWheel={e => e.currentTarget.blur()}
            disabled={!hasSelection}
            placeholder={hasSelection ? '↵' : '-'}
        />
    )
}

type OverrideField = 'limeTypeOverride' | 'limeNeedOverride' | 'gypsumNeedOverride' | 'kNeedOverride' | 'pNeedOverride' | 'elementalSulphurOverride'

// Filter popup component with local state - applies on button click
interface FilterState {
    referenceNr: string
    sampleDepth: SampleDepth | 'all'
    limeType: 'Calcitic' | 'Dolomitic' | 'all'
    limeNeedMin: number | null
    limeNeedMax: number | null
    gypsumNeedMin: number | null
    gypsumNeedMax: number | null
    kNeedMin: number | null
    kNeedMax: number | null
    pNeedMin: number | null
    pNeedMax: number | null
    elemSMin: number | null
    elemSMax: number | null
    showSelectedOnly: boolean
}

const FilterPopup: React.FC<{
    filter: FilterState
    visibleGroups: Set<ColumnGroupKey>
    onApply: (filter: FilterState) => void
    onToggleGroup: (group: ColumnGroupKey) => void
    onClose: () => void
    onResetSort: () => void
    products: ProductNames
}> = ({filter, visibleGroups, onApply, onToggleGroup, onClose, onResetSort, products}) => {
    const app = useContext(AppContext)
    const [localFilter, setLocalFilter] = useState<FilterState>(filter)

    const updateFilter = (key: keyof FilterState, value: any) => {
        setLocalFilter(prev => ({...prev, [key]: value}))
    }

    const defaultFilter: FilterState = {
        referenceNr: '',
        sampleDepth: 'all',
        limeType: 'all',
        limeNeedMin: null,
        limeNeedMax: null,
        gypsumNeedMin: null,
        gypsumNeedMax: null,
        kNeedMin: null,
        kNeedMax: null,
        pNeedMin: null,
        pNeedMax: null,
        elemSMin: null,
        elemSMax: null,
        showSelectedOnly: false
    }

    const resetFilter = () => {
        onApply(defaultFilter)
        onResetSort()
        onClose()
    }

    const inputClass = "w-16 text-xs p-1 border border-gray-300 rounded bg-white [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"

    type NumericFilterKey = keyof { [K in keyof FilterState as FilterState[K] extends number | null ? K : never]: true }

    const renderRangeInputs = (
        minKey: NumericFilterKey,
        maxKey: NumericFilterKey
    ) => (
        <div className="flex gap-1">
            <input
                type="number"
                className={inputClass}
                placeholder="min"
                value={localFilter[minKey] ?? ''}
                onChange={e => updateFilter(minKey, e.target.value === '' ? null : parseFloat(e.target.value))}
                onWheel={e => e.currentTarget.blur()}
            />
            <input
                type="number"
                className={inputClass}
                placeholder="max"
                value={localFilter[maxKey] ?? ''}
                onChange={e => updateFilter(maxKey, e.target.value === '' ? null : parseFloat(e.target.value))}
                onWheel={e => e.currentTarget.blur()}
            />
        </div>
    )

    return (
        <div className="absolute left-0 top-full mt-1 p-4 bg-white shadow-md z-50">
            <div className="text-right">
                <XMarkIcon className="cursor-pointer inline-block w-6 h-6 text-gray-400" onClick={onClose} />
            </div>

            <table className="w-full text-gray-600">
                <tbody>
                    {/* Show Columns */}
                    <tr>
                        <td className="pl-4 pr-8 font-semibold" colSpan={2}>{app.word('show')} {app.word('columns')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('soil_data')}</td>
                        <td className="py-1"><CheckBox value={visibleGroups.has('soilData')} onChange={() => onToggleGroup('soilData')} /></td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('recommendations')}</td>
                        <td className="py-1"><CheckBox value={visibleGroups.has('recommendations')} onChange={() => onToggleGroup('recommendations')} /></td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('new_nutrient_levels')}</td>
                        <td className="py-1"><CheckBox value={visibleGroups.has('nutrientLevels')} onChange={() => onToggleGroup('nutrientLevels')} /></td>
                    </tr>
                    <tr>
                    </tr>

                    {/* Filter Section */}
                    <tr>
                        <td className="pl-4 pr-8 font-semibold pt-4" colSpan={2}>{app.word('filter')} {app.word('rows')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('show')} {app.word('selected')}</td>
                        <td className="py-1"><CheckBox value={localFilter.showSelectedOnly} onChange={() => updateFilter('showSelectedOnly', !localFilter.showSelectedOnly)} /></td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('reference_nr')}</td>
                        <td className="py-1">
                            <input
                                type="text"
                                className="input"
                                placeholder={app.word('search')}
                                value={localFilter.referenceNr}
                                onChange={e => updateFilter('referenceNr', e.target.value)}
                            />
                        </td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('sample_depth')}</td>
                        <td className="py-1">
                            <select
                                className="input"
                                value={localFilter.sampleDepth}
                                onChange={e => updateFilter('sampleDepth', e.target.value)}
                            >
                                <option value="all">{app.word('all')}</option>
                                <option value={SampleDepth.TopSoil}>{app.word(SampleDepth.TopSoil)}</option>
                                <option value={SampleDepth.SubSoil}>{app.word(SampleDepth.SubSoil)}</option>
                                <option value={SampleDepth.TopSoilMicros}>{app.word(SampleDepth.TopSoilMicros)}</option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('lime_type')}</td>
                        <td className="py-1">
                            <select
                                className="input"
                                value={localFilter.limeType}
                                onChange={e => updateFilter('limeType', e.target.value)}
                            >
                                <option value="all">{app.word('all')}</option>
                                <option value="Calcitic">{products.calciticLime}</option>
                                <option value="Dolomitic">{products.dolomiticLime}</option>
                            </select>
                        </td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{app.word('lime')} {app.word('need')}</td>
                        <td className="py-1">{renderRangeInputs('limeNeedMin', 'limeNeedMax')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{products.gypsum} {app.word('need')}</td>
                        <td className="py-1">{renderRangeInputs('gypsumNeedMin', 'gypsumNeedMax')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{products.potassium} {app.word('need')}</td>
                        <td className="py-1">{renderRangeInputs('kNeedMin', 'kNeedMax')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">{products.phosphate} {app.word('need')}</td>
                        <td className="py-1">{renderRangeInputs('pNeedMin', 'pNeedMax')}</td>
                    </tr>
                    <tr>
                        <td className="pl-4 pr-8">Elem. S</td>
                        <td className="py-1">{renderRangeInputs('elemSMin', 'elemSMax')}</td>
                    </tr>
                </tbody>
            </table>

            <div className="text-right mt-2">
                <div className="inline-block px-4 cursor-pointer text-gray-600" onClick={resetFilter}>
                    {app.word('reset')}
                </div>
                <div className="btn bg-primary-500" onClick={() => { onApply(localFilter); onClose() }}>
                    {app.word('filter')}
                </div>
            </div>
        </div>
    )
}

export interface ProductNames {
    calciticLime: string; 
    dolomiticLime: string; 
    gypsum: string; 
    phosphate: string; 
    potassium: string;
}

interface LimePlayfieldUnifiedProps {
    data: LimeProgramLabInputs
    setData: (data: LimeProgramLabInputs) => void
    calculatedRows: LimePlayFieldRow[]
    updateRowOverride: (index: number, field: OverrideField, value: string | number | null) => void
    updateMultipleRowOverrides: (indices: number[], field: OverrideField, value: string | number | null) => void
    labInputUnit?: LabInputUnit
    landIds?: number[]
    products: ProductNames
    totalRows?: number
}

const LimePlayfieldUnified: React.FC<LimePlayfieldUnifiedProps> = (props) => {
    const app = useContext(AppContext)
    const system = app.initial.system

    // Unit conversion for rate values (kg/ha ↔ lb/ac)
    const rateLabel = unitCustom(system, customUnits.kgha_lbAc)
    const toDisplayRate = (value: number): number =>
        system === 'METRIC' ? value : customUnits.kgha_lbAc.metric.invert(value)
    const toStorageRate = (value: number): number =>
        system === 'METRIC' ? value : customUnits.kgha_lbAc.imperial.invert(value)
    const formatRate = (value: number): string => {
        const converted = toDisplayRate(value)
        return converted > 0 ? converted.toFixed(0) : '-'
    }

    // Internal state for row management
    const [rows, setRowsState] = useState<RowWithId[]>(() => toRowsWithId(props.data))
    const [showUpload, setShowUpload] = useState(false)
    const [showEdit, setShowEdit] = useState(false)
    const [editRow, setEditRow] = useState<RowWithId>(createEmptyRow(0))
    const [editIndex, setEditIndex] = useState<number>(-1)
    const [showMap, setShowMap] = useState(false)
    const [showFilter, setShowFilter] = useState(false)

    // Column group visibility state - all visible by default
    const [visibleGroups, setVisibleGroups] = useState<Set<ColumnGroupKey>>(
        new Set<ColumnGroupKey>(['soilData', 'recommendations', 'nutrientLevels'])
    )

    // Selection state
    const [selectedRows, setSelectedRows] = useState<Set<number>>(new Set())

    // Filter state
    const [referenceNrFilter, setReferenceNrFilter] = useState<string>('')
    const [sampleDepthFilter, setSampleDepthFilter] = useState<SampleDepth | 'all'>('all')
    const [limeTypeFilter, setLimeTypeFilter] = useState<'Calcitic' | 'Dolomitic' | 'all'>('all')
    const [limeNeedMin, setLimeNeedMin] = useState<number | null>(null)
    const [limeNeedMax, setLimeNeedMax] = useState<number | null>(null)
    const [gypsumNeedMin, setGypsumNeedMin] = useState<number | null>(null)
    const [gypsumNeedMax, setGypsumNeedMax] = useState<number | null>(null)
    const [kNeedMin, setKNeedMin] = useState<number | null>(null)
    const [kNeedMax, setKNeedMax] = useState<number | null>(null)
    const [pNeedMin, setPNeedMin] = useState<number | null>(null)
    const [pNeedMax, setPNeedMax] = useState<number | null>(null)
    const [elemSMin, setElemSMin] = useState<number | null>(null)
    const [elemSMax, setElemSMax] = useState<number | null>(null)
    const [showSelectedOnly, setShowSelectedOnly] = useState(false)

    // Sort state
    const [sortColumn, setSortColumn] = useState<SortableColumn | null>(null)
    const [sortDirection, setSortDirection] = useState<SortDirection>(null)

    // Memoize sample points
    const samplePoints = useMemo(() => rowsToSamplePoints(rows), [rows])

    // Filter rows based on selected filters
    const filteredRowsWithIndex = useMemo(() => {
        const filtered = rows.map((row, idx) => ({row, idx})).filter(({row, idx}) => {
            // Show selected only filter
            if (showSelectedOnly && !selectedRows.has(idx)) {
                return false
            }
            // Reference number filter (case-insensitive partial match)
            if (referenceNrFilter && !row.referenceNr.toLowerCase().includes(referenceNrFilter.toLowerCase())) {
                return false
            }
            // Sample depth filter
            if (sampleDepthFilter !== 'all' && row.sampleDepth !== sampleDepthFilter) {
                return false
            }
            const calc = props.calculatedRows[idx]
            // Lime type filter (based on calculated rows)
            if (limeTypeFilter !== 'all') {
                if (!calc) return false
                if (limeTypeFilter === 'Calcitic' && calc.limeType === 'Dolomitic') return false
                if (limeTypeFilter === 'Dolomitic' && calc.limeType !== 'Dolomitic') return false
            }
            // Lime need range filter (filter values are in user's unit)
            if (limeNeedMin !== null || limeNeedMax !== null) {
                if (!calc) return false
                const limeNeed = toDisplayRate(calc.amendmentNeedKgHa)
                if (limeNeedMin !== null && limeNeed < limeNeedMin) return false
                if (limeNeedMax !== null && limeNeed > limeNeedMax) return false
            }
            // Gypsum need range filter
            if (gypsumNeedMin !== null || gypsumNeedMax !== null) {
                if (!calc) return false
                const gypsumNeed = toDisplayRate(calc.gypsumNeedKgHa)
                if (gypsumNeedMin !== null && gypsumNeed < gypsumNeedMin) return false
                if (gypsumNeedMax !== null && gypsumNeed > gypsumNeedMax) return false
            }
            // K need range filter
            if (kNeedMin !== null || kNeedMax !== null) {
                if (!calc) return false
                const kNeed = toDisplayRate(calc.kNeedKgHa)
                if (kNeedMin !== null && kNeed < kNeedMin) return false
                if (kNeedMax !== null && kNeed > kNeedMax) return false
            }
            // P need range filter
            if (pNeedMin !== null || pNeedMax !== null) {
                if (!calc) return false
                const pNeed = toDisplayRate(calc.pNeedKgHa)
                if (pNeedMin !== null && pNeed < pNeedMin) return false
                if (pNeedMax !== null && pNeed > pNeedMax) return false
            }
            // Elemental sulphur range filter
            if (elemSMin !== null || elemSMax !== null) {
                if (!calc) return false
                const elemS = toDisplayRate(calc.elementalSulphurKgHa)
                if (elemSMin !== null && elemS < elemSMin) return false
                if (elemSMax !== null && elemS > elemSMax) return false
            }
            return true
        })

        // Apply sorting (default: by reference number)
        if (sortColumn && sortDirection) {
            filtered.sort((a, b) => {
                if (STRING_SORT_COLUMNS.has(sortColumn)) {
                    const aStr = (a.row[sortColumn as keyof RowWithId] as string) ?? ''
                    const bStr = (b.row[sortColumn as keyof RowWithId] as string) ?? ''
                    return sortDirection === 'asc' ? aStr.localeCompare(bStr) : bStr.localeCompare(aStr)
                }
                let aVal: number, bVal: number
                if (isSoilColumn(sortColumn)) {
                    aVal = getSoilSortValue(a.row, sortColumn, system)
                    bVal = getSoilSortValue(b.row, sortColumn, system)
                } else {
                    aVal = getCalcSortValue(props.calculatedRows[a.idx], sortColumn)
                    bVal = getCalcSortValue(props.calculatedRows[b.idx], sortColumn)
                }
                return sortDirection === 'asc' ? aVal - bVal : bVal - aVal
            })
        } else {
            filtered.sort((a, b) => a.row.referenceNr.localeCompare(b.row.referenceNr))
        }

        return filtered
    }, [rows, referenceNrFilter, sampleDepthFilter, limeTypeFilter, limeNeedMin, limeNeedMax, gypsumNeedMin, gypsumNeedMax, kNeedMin, kNeedMax, pNeedMin, pNeedMax, elemSMin, elemSMax, showSelectedOnly, selectedRows, props.calculatedRows, sortColumn, sortDirection, system])

    // Check if any filters are active (used for indicator badge and row count display)
    const hasActiveFilters = referenceNrFilter !== '' ||
        sampleDepthFilter !== 'all' || limeTypeFilter !== 'all' ||
        limeNeedMin !== null || limeNeedMax !== null ||
        gypsumNeedMin !== null || gypsumNeedMax !== null ||
        kNeedMin !== null || kNeedMax !== null ||
        pNeedMin !== null || pNeedMax !== null ||
        elemSMin !== null || elemSMax !== null ||
        showSelectedOnly

    // meq/100g mode for K, Na, Ca, Mg
    const meq100gMode = isMeq100g(props.labInputUnit)

    // Get the unit label
    const unitLabel = meq100gMode ? 'meq/100g' : unitCustom(system, customUnits.mgKg_ppm)

    function setRows(newRows: RowWithId[]) {
        setRowsState(newRows)
        props.setData(toLabInputs(newRows))
    }

    // Keep internal rows in sync with props
    React.useEffect(() => {
        setRowsState(toRowsWithId(props.data))
    }, [props.data])

    // Toggle column group visibility
    function toggleGroup(group: ColumnGroupKey) {
        setVisibleGroups(prev => {
            const next = new Set<ColumnGroupKey>(prev)
            if (next.has(group)) {
                next.delete(group)
            } else {
                next.add(group)
            }
            return next
        })
    }

    function th(
        bg: string,
        children: React.ReactNode,
        opts: {rowSpan?: number, colSpan?: number, sort?: SortableColumn, variant?: 'sub' | 'subAlt' | 'group'} = {}
    ) {
        const {rowSpan, colSpan, sort, variant = 'sub'} = opts
        const base = variant === 'subAlt' ? tableStyles.headerSubAlt : variant === 'group' ? tableStyles.headerGroup : tableStyles.headerSub
        const className = sort ? `${base} cursor-pointer hover:bg-gray-300` : base
        return (
            <th rowSpan={rowSpan} colSpan={colSpan} className={className} style={{background: bg}} onClick={sort ? () => toggleSort(sort) : undefined}>
                {children}
                {sort && <SortIndicator column={sort} sortColumn={sortColumn} sortDirection={sortDirection}/>}
            </th>
        )
    }

    // Toggle sort for a column
    function toggleSort(column: SortableColumn) {
        if (sortColumn !== column) {
            // New column - start with descending (high to low)
            setSortColumn(column)
            setSortDirection('desc')
        } else if (sortDirection === 'desc') {
            // Same column, was descending - switch to ascending
            setSortDirection('asc')
        } else {
            // Same column, was ascending - clear sort
            setSortColumn(null)
            setSortDirection(null)
        }
    }

    // Apply bulk override to all selected rows (convert rate values to kg/ha for storage)
    function applyBulkOverride(field: OverrideField, value: string | number | null) {
        const converted = field !== 'limeTypeOverride' && typeof value === 'number'
            ? toStorageRate(value)
            : value
        props.updateMultipleRowOverrides(Array.from(selectedRows), field, converted)
    }

    // Helper for NullableMeasureValue fields in edit dialog
    function nullableMeasure(label: string, key: KeyOfType<RowWithId, NullableMeasureValue>) {
        return <td>
            <div>{label}</div>
            <NullableMeasureValueField
                unit={customUnits.mgKg_ppm}
                value={editRow[key]}
                update={v => setEditRow({...editRow, [key]: v})}
            />
        </td>
    }

    // Helper for nullable number fields in edit dialog
    function nullableNumber(label: string, key: KeyOfType<RowWithId, number | null>) {
        return <td>
            <div>{label}</div>
            <NumberNullable value={editRow[key]} change={v => setEditRow({...editRow, [key]: v})}/>
        </td>
    }

    function handleCsvUpload(file: File) {
        const reader = new FileReader()
        reader.onload = (e) => {
            const text = e.target?.result as string
            const minId = min(rows, r => r._id, 0) - 1
            const columnMatchers = buildLimeSoilColumnMatchers(meq100gMode)
            const newRows = parseCSVFile<RowWithId>(
                text,
                (index) => createEmptyRow(minId - index),
                system,
                columnMatchers
            )
            setRows([...rows, ...newRows])
            setShowUpload(false)
        }
        reader.readAsText(file)
    }

    function addRow() {
        const minId = min(rows, r => r._id, 0) - 1
        setEditRow(createEmptyRow(minId))
        setEditIndex(-1)
        setShowEdit(true)
    }

    function onEdit(row: RowWithId, index: number) {
        setEditRow(convertRowForEditing(row, meq100gMode))
        setEditIndex(index)
        setShowEdit(true)
    }

    function onRemove(index: number) {
        setRows(arrayRemoveIndex(rows, index))
    }

    function saveEdit() {
        const rowToSave = convertRowForSaving(editRow, meq100gMode)

        if (editIndex === -1) {
            setRows(arrayPush(rows, rowToSave))
        } else {
            setRows(arrayUpdate(rows, editIndex, rowToSave))
        }
        setShowEdit(false)
    }

    function formatMeasure(value: NullableMeasureValue): string {
        const v = getValueNullable(system, value)
        if (v === null) return '-'
        return v.toFixed(2)
    }

    // Count columns for header spanning
    const soilDataColCount = 20 // Depth, pH, P, K, Na, Ca, Mg, Exch H, %Ca, %Mg, %K, %Na, Acid Sat, Ca:Mg, (Ca+Mg)/K, Mg:K, S-Value, Na:K, CEC, Density, Sulfur
    const recommendationsColCount = 18 // Lime type calc/override, Lime need/override/Y1/Y2, Gypsum need/override/Y1, K need/override/Y1, P need/override/Y1, Elem S calc/override/applied
    const nutrientLevelsColCount = 18 // mg/kg Y1 (5) + mg/kg Y2 (5) + %Sat Y1 (4) + %Sat Y2 (4)

    return <div className="my-2 flex-1 min-h-0 flex flex-col">
        {/* CSV Upload Dialog */}
        <Dialog
            title={app.word('upload_csv')}
            show={showUpload}
            setShow={setShowUpload}
            body={
                <div className="p-4">
                    <div>{app.word('download_csv_sample')}, {app.word('add_data_and_upload_again')}.</div>
                    <a className="underline" href="/LimeSoilSample.csv" download="LimeSoilSample.csv">LimeSoilSample.csv</a>
                    <div className="my-2">
                        <FileUploader onChange={file => handleCsvUpload(file)} fileTypes=".csv"/>
                    </div>
                </div>
            }
        />

        {/* Edit Row Dialog */}
        <Dialog
            title={editIndex === -1 ? app.word('add') : app.word('edit')}
            show={showEdit}
            setShow={setShowEdit}
            body={
                <div className="p-4 max-h-[70vh] overflow-y-auto">
                    <table className="w-full">
                        <tbody>
                            <tr>
                                <td>
                                    <div>{app.word('block')}</div>
                                    <div className="input bg-gray-100">{editRow.landName || '—'}</div>
                                </td>
                                <td>
                                    <div>{app.word('reference_nr')}</div>
                                    <input className="input" type="text" value={editRow.referenceNr}
                                           onInput={onInputHandler(v => setEditRow({...editRow, referenceNr: v}))}/>
                                </td>
                                {nullableNumber(app.word('latitude'), 'latitude')}
                                {nullableNumber(app.word('longitude'), 'longitude')}
                                <td>
                                    <div>{app.word('sample_depth')}</div>
                                    <select
                                        className="input"
                                        value={editRow.sampleDepth}
                                        onChange={e => setEditRow({...editRow, sampleDepth: e.target.value as SampleDepth})}
                                    >
                                        <option value={SampleDepth.TopSoil}>{app.word(SampleDepth.TopSoil)}</option>
                                        <option value={SampleDepth.SubSoil}>{app.word(SampleDepth.SubSoil)}</option>
                                        <option value={SampleDepth.TopSoilMicros}>{app.word(SampleDepth.TopSoilMicros)}</option>
                                    </select>
                                </td>
                                <td>
                                    <div>pH(H2O)</div>
                                    <Number value={editRow.phH2O} change={v => setEditRow({...editRow, phH2O: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure('P Bray I', 'pBrayI')}
                                {nullableMeasure(`K (${unitLabel})`, 'k')}
                                {nullableMeasure(`Na (${unitLabel})`, 'na')}
                                {nullableMeasure(`Ca (${unitLabel})`, 'ca')}
                                {nullableMeasure(`Mg (${unitLabel})`, 'mg')}
                                <td>
                                    <div>Exch H+</div>
                                    <Number value={editRow.exchH} change={v => setEditRow({...editRow, exchH: v})}/>
                                </td>
                            </tr>

                            <tr>
                                {nullableMeasure(app.word('sulfur'), 'sulfur')}
                                <td>
                                    <div>{app.word('density')}</div>
                                    <Number value={editRow.density} change={v => setEditRow({...editRow, density: v})}/>
                                </td>
                                {nullableNumber('%Ca', 'caPercent')}
                                {nullableNumber('%Mg', 'mgPercent')}
                                {nullableNumber('%K', 'kPercent')}
                                {nullableNumber('%Na', 'naPercent')}
                            </tr>

                            <tr>
                                {nullableNumber(app.word('acid_saturated'), 'acidSat')}
                                {nullableNumber('Ca:Mg', 'caMgRatio')}
                                {nullableNumber('(Ca+Mg)/K', 'caMgKRatio')}
                                {nullableNumber('Mg:K', 'mgKRatio')}
                                {nullableNumber('S-Value', 'sValue')}
                                {nullableNumber('Na:K', 'naKRatio')}
                            </tr>

                            <tr>
                                {nullableNumber('CEC', 'cec')}
                            </tr>
                        </tbody>
                    </table>

                    <div className="flex justify-end items-end">
                        <button className="btn-sm bg-red-500" onClick={() => setShowEdit(false)}>
                            {app.word('discard')}
                        </button>
                        <button className="btn-sm bg-primary" onClick={saveEdit}>
                            {app.word('save')}
                        </button>
                    </div>
                </div>
            }
        />

        {/* Map Dialog */}
        <SampleLocationsDialog
            show={showMap}
            setShow={setShowMap}
            rows={rows}
            samplePoints={samplePoints}
            landIds={props.landIds}
            meq100gMode={meq100gMode}
        />

        {/* Filter Button and Popup */}
        <div className="relative inline-block mb-3">
            <button
                className="flex items-center gap-1 px-3 py-1 text-sm text-gray-600 border border-gray-300 rounded hover:bg-gray-100"
                onClick={() => setShowFilter(!showFilter)}
            >
                <FunnelIcon className="w-4 h-4" />
                {app.word('filter')}
                {hasActiveFilters && (
                    <span className="ml-1 px-1.5 py-0.5 text-xs bg-primary-500 text-white rounded-full">!</span>
                )}
            </button>

            {showFilter && (
                <FilterPopup
                    filter={{
                        referenceNr: referenceNrFilter,
                        sampleDepth: sampleDepthFilter,
                        limeType: limeTypeFilter,
                        limeNeedMin,
                        limeNeedMax,
                        gypsumNeedMin,
                        gypsumNeedMax,
                        kNeedMin,
                        kNeedMax,
                        pNeedMin,
                        pNeedMax,
                        elemSMin,
                        elemSMax,
                        showSelectedOnly
                    }}
                    visibleGroups={visibleGroups}
                    onApply={(f) => {
                        setReferenceNrFilter(f.referenceNr)
                        setSampleDepthFilter(f.sampleDepth)
                        setLimeTypeFilter(f.limeType)
                        setLimeNeedMin(f.limeNeedMin)
                        setLimeNeedMax(f.limeNeedMax)
                        setGypsumNeedMin(f.gypsumNeedMin)
                        setGypsumNeedMax(f.gypsumNeedMax)
                        setKNeedMin(f.kNeedMin)
                        setKNeedMax(f.kNeedMax)
                        setPNeedMin(f.pNeedMin)
                        setPNeedMax(f.pNeedMax)
                        setElemSMin(f.elemSMin)
                        setElemSMax(f.elemSMax)
                        setShowSelectedOnly(f.showSelectedOnly)
                    }}
                    onToggleGroup={toggleGroup}
                    onClose={() => setShowFilter(false)}
                    onResetSort={() => { setSortColumn(null); setSortDirection(null) }}
                    products={props.products}
                />
            )}
        </div>

        {/* Show matched/total row counts */}
        {props.totalRows !== undefined && props.totalRows !== rows.length && (
            <span className="ml-2 text-sm text-gray-500">
                ({rows.length} / {props.totalRows} {app.word('matched')})
            </span>
        )}
        {hasActiveFilters && (
            <span className="ml-2 text-sm text-gray-500">
                ({filteredRowsWithIndex.length} / {rows.length} {app.word('rows')})
            </span>
        )}
        {selectedRows.size > 0 && (
            <span className="ml-2 text-sm text-gray-500">
                {selectedRows.size} selected
                <span className="ml-1 underline cursor-pointer text-primary" onClick={() => setSelectedRows(new Set())}>
                    {app.word('clear')}
                </span>
            </span>
        )}

        {rows.length === 0 ? (
            <div>{app.word('no_soil_data')}</div>
        ) : (
            <div className="min-h-0 overflow-y-auto">
                <table className="w-full border-collapse">
                    {/* Table Header - Level 1: Group Headers */}
                    <thead className="sticky top-0 z-30">
                        <tr>
                            {/* Select column */}
                            <th rowSpan={3} className={`${tableStyles.stickyHeader} left-0 bg-gray-200`} style={{minWidth: '40px'}}>
                                <CheckBox
                                    value={filteredRowsWithIndex.length > 0 && filteredRowsWithIndex.every(({idx}) => selectedRows.has(idx))}
                                    onChange={checked => {
                                        const newSelected = new Set(selectedRows)
                                        if (checked) {
                                            filteredRowsWithIndex.forEach(({idx}) => newSelected.add(idx))
                                        } else {
                                            filteredRowsWithIndex.forEach(({idx}) => newSelected.delete(idx))
                                        }
                                        setSelectedRows(newSelected)
                                    }}
                                />
                            </th>
                            {/* Identity columns (always visible, sticky) */}
                            <th rowSpan={3} className={`${tableStyles.stickyHeader} left-[40px] bg-gray-200 cursor-pointer hover:bg-gray-300`} style={{minWidth: '80px'}} onClick={() => toggleSort('landName')}>
                                {app.word('land_ref')}
                                <SortIndicator column="landName" sortColumn={sortColumn} sortDirection={sortDirection}/>
                            </th>
                            <th rowSpan={3} className={`${tableStyles.stickyHeader} left-[120px] bg-gray-200 cursor-pointer hover:bg-gray-300`} style={{minWidth: '80px'}} onClick={() => toggleSort('referenceNr')}>
                                {app.word('reference_nr')}
                                <SortIndicator column="referenceNr" sortColumn={sortColumn} sortDirection={sortDirection}/>
                            </th>

                            {/* Soil Data Group */}
                            {visibleGroups.has('soilData') && th('#C3BD9B', app.word('soil_data'), {colSpan: soilDataColCount, variant: 'group'})}

                            {/* Recommendations Group */}
                            {visibleGroups.has('recommendations') && th('#ECF1E0', app.word('recommendations'), {colSpan: recommendationsColCount, variant: 'group'})}

                            {/* New Nutrient Levels Group */}
                            {visibleGroups.has('nutrientLevels') && th('#E3DFEB', app.word('new_nutrient_levels'), {colSpan: nutrientLevelsColCount, variant: 'group'})}

                            {/* Actions column (always visible) */}
                            <th rowSpan={3} className={tableStyles.headerGroup}>
                                {app.word('actions')}
                            </th>
                        </tr>

                        {/* Table Header - Level 2: Soil Data + Recommendations sub-columns, Nutrient sub-groups */}
                        <tr>
                            {/* Soil Data column headers (rowSpan 2 to span into row 3) */}
                            {visibleGroups.has('soilData') && (
                                <>
                                    {th('#C3BD9B', app.word('sample_depth'), {rowSpan: 2, sort: 'sampleDepth'})}
                                    {th('#C3BD9B', 'pH(H2O)', {rowSpan: 2, sort: 'phH2O'})}
                                    {th('#C3BD9B', 'P Bray I', {rowSpan: 2, sort: 'pBrayI'})}
                                    {th('#C3BD9B', `K (${unitLabel})`, {rowSpan: 2, sort: 'k'})}
                                    {th('#C3BD9B', `Na (${unitLabel})`, {rowSpan: 2, sort: 'na'})}
                                    {th('#C3BD9B', `Ca (${unitLabel})`, {rowSpan: 2, sort: 'ca'})}
                                    {th('#C3BD9B', `Mg (${unitLabel})`, {rowSpan: 2, sort: 'mg'})}
                                    {th('#C3BD9B', 'Exch H', {rowSpan: 2, sort: 'exchH'})}
                                    {th('#C3BD9B', '%Ca', {rowSpan: 2, sort: 'caPercent'})}
                                    {th('#C3BD9B', '%Mg', {rowSpan: 2, sort: 'mgPercent'})}
                                    {th('#C3BD9B', '%K', {rowSpan: 2, sort: 'kPercent'})}
                                    {th('#C3BD9B', '%Na', {rowSpan: 2, sort: 'naPercent'})}
                                    {th('#C3BD9B', app.word('acid_saturated'), {rowSpan: 2, sort: 'acidSat'})}
                                    {th('#C3BD9B', 'Ca:Mg', {rowSpan: 2, sort: 'caMgRatio'})}
                                    {th('#C3BD9B', '(Ca+Mg)/K', {rowSpan: 2, sort: 'caMgKRatio'})}
                                    {th('#C3BD9B', 'Mg:K', {rowSpan: 2, sort: 'mgKRatio'})}
                                    {th('#C3BD9B', 'S-Value', {rowSpan: 2, sort: 'sValue'})}
                                    {th('#C3BD9B', 'Na:K', {rowSpan: 2, sort: 'naKRatio'})}
                                    {th('#C3BD9B', 'CEC', {rowSpan: 2, sort: 'cec'})}
                                    {th('#C3BD9B', app.word('density'), {rowSpan: 2, sort: 'density'})}
                                </>
                            )}

                            {/* Recommendations column headers (rowSpan 2 to span into row 3) */}
                            {visibleGroups.has('recommendations') && (
                                <>
                                    {/* Lime type */}
                                    {th('#ECF1E0', app.word('lime_type'), {rowSpan: 2, sort: 'limeType'})}
                                    {th('#ECF1E0', <>
                                        <div>{app.word('override')}</div>
                                        <BulkOverrideInput
                                            type="select"
                                            options={[{value: 'Calcitic', label: props.products.calciticLime}, {value: 'Dolomitic', label: props.products.dolomiticLime}]}
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('limeTypeOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {/* Lime need */}
                                    {th('#ECF1E0', `${app.word('lime')} ${app.word('need')} (${rateLabel})`, {rowSpan: 2, sort: 'amendmentNeedKgHa'})}
                                    {th('#ECF1E0', <>
                                        <div>{app.word('override')} ({rateLabel})</div>
                                        <BulkOverrideInput
                                            type="number"
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('limeNeedOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {th('#ECF1E0', `${app.word('lime')} Y1 (${rateLabel})`, {rowSpan: 2, sort: 'appliedYear1KgHa'})}
                                    {th('#ECF1E0', `${app.word('lime')} Y2 (${rateLabel})`, {rowSpan: 2, sort: 'appliedYear2KgHa'})}
                                    {/* Gypsum */}
                                    {th('#EEDDDC', `${props.products.gypsum} ${app.word('need')} (${rateLabel})`, {rowSpan: 2, sort: 'gypsumNeedKgHa'})}
                                    {th('#EEDDDC', <>
                                        <div>{app.word('override')} ({rateLabel})</div>
                                        <BulkOverrideInput
                                            type="number"
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('gypsumNeedOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {th('#EEDDDC', `${props.products.gypsum} Y1 (${rateLabel})`, {rowSpan: 2, sort: 'gypsumYear1KgHa'})}
                                    {/* K */}
                                    {th('#E3DFEB', `${props.products.potassium} ${app.word('need')} (${rateLabel})`, {rowSpan: 2, sort: 'kNeedKgHa'})}
                                    {th('#E3DFEB', <>
                                        <div>{app.word('override')} ({rateLabel})</div>
                                        <BulkOverrideInput
                                            type="number"
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('kNeedOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {th('#E3DFEB', `${props.products.potassium} Y1 (${rateLabel})`, {rowSpan: 2, sort: 'kFertYear1KgHa'})}
                                    {/* P */}
                                    {th('#4FADEA', `${props.products.phosphate} ${app.word('need')} (${rateLabel})`, {rowSpan: 2, sort: 'pNeedKgHa'})}
                                    {th('#4FADEA', <>
                                        <div>{app.word('override')} ({rateLabel})</div>
                                        <BulkOverrideInput
                                            type="number"
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('pNeedOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {th('#4FADEA', `${props.products.phosphate} Y1 (${rateLabel})`, {rowSpan: 2, sort: 'pFertYear1KgHa'})}
                                    {/* Elemental S */}
                                    {th('#4A8499', `Elem. S ${app.word('calc')} (${rateLabel})`, {rowSpan: 2, sort: 'elementalSulphurKgHa'})}
                                    {th('#4A8499', <>
                                        <div>{app.word('override')} ({rateLabel})</div>
                                        <BulkOverrideInput
                                            type="number"
                                            hasSelection={selectedRows.size > 0}
                                            onApply={v => applyBulkOverride('elementalSulphurOverride', v)}
                                        />
                                    </>, {rowSpan: 2, variant: 'subAlt'})}
                                    {th('#4A8499', `Elem. S ${app.word('applied')} (${rateLabel})`, {rowSpan: 2, sort: 'elementalSulphurKgHa'})}
                                </>
                            )}

                            {/* Nutrient Levels sub-group headers */}
                            {visibleGroups.has('nutrientLevels') && (
                                <>
                                    {th('#E3DFEB', `${unitCustom(system, customUnits.mgKg_ppm)} ${app.word('year_one')}`, {colSpan: 5, variant: 'group'})}
                                    {th('#AEA1C4', `${unitCustom(system, customUnits.mgKg_ppm)} ${app.word('year_two')}`, {colSpan: 5, variant: 'group'})}
                                    {th('#E3DFEB', `${app.word('percentage_sat')} ${app.word('year_one')}`, {colSpan: 4, variant: 'group'})}
                                    {th('#AEA1C4', `${app.word('percentage_sat')} ${app.word('year_two')}`, {colSpan: 4, variant: 'group'})}
                                </>
                            )}
                        </tr>

                        {/* Table Header - Level 3: Nutrient Levels individual column headers */}
                        <tr>
                            {visibleGroups.has('nutrientLevels') && (
                                <>
                                    {/* mg/kg Y1 */}
                                    {th('#E3DFEB', 'Ca', {sort: 'newCaMgKg'})}
                                    {th('#E3DFEB', 'Mg', {sort: 'newMgMgKg'})}
                                    {th('#E3DFEB', 'K', {sort: 'newKMgKg'})}
                                    {th('#E3DFEB', 'P', {sort: 'newPMgKg'})}
                                    {th('#E3DFEB', 'Na', {sort: 'newNaMgKg'})}
                                    {/* mg/kg Y2 */}
                                    {th('#AEA1C4', 'Ca', {sort: 'newCaMgKgY2'})}
                                    {th('#AEA1C4', 'Mg', {sort: 'newMgMgKgY2'})}
                                    {th('#AEA1C4', 'K', {sort: 'newKMgKgY2'})}
                                    {th('#AEA1C4', 'P', {sort: 'newPMgKgY2'})}
                                    {th('#AEA1C4', 'Na', {sort: 'newNaMgKgY2'})}
                                    {/* % Sat Y1 */}
                                    {th('#E3DFEB', 'Ca%', {sort: 'satAfterCa'})}
                                    {th('#E3DFEB', 'Mg%', {sort: 'satAfterMg'})}
                                    {th('#E3DFEB', 'K%', {sort: 'satAfterK'})}
                                    {th('#E3DFEB', 'Na%', {sort: 'satAfterNa'})}
                                    {/* % Sat Y2 */}
                                    {th('#AEA1C4', 'Ca%', {sort: 'satAfterCaY2'})}
                                    {th('#AEA1C4', 'Mg%', {sort: 'satAfterMgY2'})}
                                    {th('#AEA1C4', 'K%', {sort: 'satAfterKY2'})}
                                    {th('#AEA1C4', 'Na%', {sort: 'satAfterNaY2'})}
                                </>
                            )}
                        </tr>
                    </thead>

                    {/* Table Body */}
                    <tbody>
                        {filteredRowsWithIndex.map(({row, idx}) => {
                            const calc = props.calculatedRows[idx]
                            const labRow = props.data.rows[idx]
                            const isCal = calc?.limeType !== 'Dolomitic'

                            return (
                                <tr key={row._id}
                                    className={`${selectedRows.has(idx) ? 'bg-primary-100' : 'hover:bg-gray-200'}`}
                                >
                                    {/* Select checkbox */}
                                    <td className={`${tableStyles.stickyCell} left-0`} style={{minWidth: '40px'}}>
                                        <CheckBox
                                            value={selectedRows.has(idx)}
                                            onChange={checked => {
                                                const newSelected = new Set(selectedRows)
                                                if (checked) {
                                                    newSelected.add(idx)
                                                } else {
                                                    newSelected.delete(idx)
                                                }
                                                setSelectedRows(newSelected)
                                            }}
                                        />
                                    </td>
                                    {/* Identity columns (sticky) */}
                                    <td className={`${tableStyles.stickyCell} left-[40px] ${selectedRows.has(idx) ? '!bg-primary-100' : ''}`} style={{minWidth: '80px'}}>
                                        {row.landName}
                                    </td>
                                    <td className={`${tableStyles.stickyCell} left-[120px] border-r-2 border-gray-300 ${selectedRows.has(idx) ? '!bg-primary-100' : ''}`} style={{minWidth: '80px'}}>
                                        {row.referenceNr}
                                    </td>

                                    {/* Soil Data cells */}
                                    {visibleGroups.has('soilData') && (
                                        <>
                                            <td className={tableStyles.cell}>{app.word(row.sampleDepth)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.phH2O)}</td>
                                            <td className={tableStyles.cellRight}>{formatMeasure(row.pBrayI)}</td>
                                            <td className={tableStyles.cellRight}>{formatMeasureElement(system, row.k, 'K', meq100gMode)}</td>
                                            <td className={tableStyles.cellRight}>{formatMeasureElement(system, row.na, 'Na', meq100gMode)}</td>
                                            <td className={tableStyles.cellRight}>{formatMeasureElement(system, row.ca, 'Ca', meq100gMode)}</td>
                                            <td className={tableStyles.cellRight}>{formatMeasureElement(system, row.mg, 'Mg', meq100gMode)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.exchH)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.caPercent)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.mgPercent)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.kPercent)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.naPercent)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.acidSat)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.caMgRatio)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.caMgKRatio)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.mgKRatio)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.sValue)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.naKRatio)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.cec)}</td>
                                            <td className={tableStyles.cellRight}>{formatValue(row.density)}</td>
                                        </>
                                    )}

                                    {/* Recommendations cells */}
                                    {visibleGroups.has('recommendations') && calc && (
                                        <>
                                            {/* Lime type */}
                                            <td className={`${tableStyles.cell} text-center`}>
                                                {calc.limeType === 'Dolomitic' ? props.products.dolomiticLime : props.products.calciticLime}
                                            </td>
                                            <td className={tableStyles.cellOverride}>
                                                <select
                                                    className={tableStyles.select}
                                                    value={labRow?.limeTypeOverride ?? ''}
                                                    onChange={e => props.updateRowOverride(idx, 'limeTypeOverride', e.target.value || null)}
                                                >
                                                    <option value="">-</option>
                                                    <option value="Calcitic">{props.products.calciticLime}</option>
                                                    <option value="Dolomitic">{props.products.dolomiticLime}</option>
                                                </select>
                                            </td>
                                            {/* Lime need */}
                                            <td className={tableStyles.cellRight}>{formatRate(calc.amendmentNeedKgHa)}</td>
                                            <td className={tableStyles.cellOverride}>
                                                <OverrideInput
                                                    value={labRow?.limeNeedOverride}
                                                    onChange={v => props.updateRowOverride(idx, 'limeNeedOverride', v)}
                                                    toDisplay={toDisplayRate}
                                                    toStorage={toStorageRate}
                                                />
                                            </td>
                                            <td className={tableStyles.cellRightBold}>{formatRate(calc.appliedYear1KgHa)}</td>
                                            <td className={tableStyles.cellRight}>{formatRate(calc.appliedYear2KgHa)}</td>
                                            {/* Gypsum */}
                                            <td className={tableStyles.cellRight}>{formatRate(calc.gypsumNeedKgHa)}</td>
                                            <td className={tableStyles.cellOverride}>
                                                <OverrideInput
                                                    value={labRow?.gypsumNeedOverride}
                                                    onChange={v => props.updateRowOverride(idx, 'gypsumNeedOverride', v)}
                                                    toDisplay={toDisplayRate}
                                                    toStorage={toStorageRate}
                                                />
                                            </td>
                                            <td className={tableStyles.cellRightBold}>{formatRate(calc.gypsumYear1KgHa)}</td>
                                            {/* K */}
                                            <td className={tableStyles.cellRight}>{formatRate(calc.kNeedKgHa)}</td>
                                            <td className={tableStyles.cellOverride}>
                                                <OverrideInput
                                                    value={labRow?.kNeedOverride}
                                                    onChange={v => props.updateRowOverride(idx, 'kNeedOverride', v)}
                                                    toDisplay={toDisplayRate}
                                                    toStorage={toStorageRate}
                                                />
                                            </td>
                                            <td className={tableStyles.cellRightBold}>{formatRate(calc.kFertYear1KgHa)}</td>
                                            {/* P */}
                                            <td className={tableStyles.cellRight}>{formatRate(calc.pNeedKgHa)}</td>
                                            <td className={tableStyles.cellOverride}>
                                                <OverrideInput
                                                    value={labRow?.pNeedOverride}
                                                    onChange={v => props.updateRowOverride(idx, 'pNeedOverride', v)}
                                                    toDisplay={toDisplayRate}
                                                    toStorage={toStorageRate}
                                                />
                                            </td>
                                            <td className={tableStyles.cellRightBold}>{formatRate(calc.pFertYear1KgHa)}</td>
                                            {/* Elemental S */}
                                            <td className={tableStyles.cellRight}>{formatRate(calcElementalSulphurNeed(calc.phKcl, calc.phOperator))}</td>
                                            <td className={tableStyles.cellOverride}>
                                                <OverrideInput
                                                    value={labRow?.elementalSulphurOverride}
                                                    onChange={v => props.updateRowOverride(idx, 'elementalSulphurOverride', v)}
                                                    toDisplay={toDisplayRate}
                                                    toStorage={toStorageRate}
                                                />
                                            </td>
                                            <td className={tableStyles.cellRightBold}>{formatRate(calc.elementalSulphurKgHa)}</td>
                                        </>
                                    )}

                                    {/* Show placeholder cells if no calc data but recommendations visible */}
                                    {visibleGroups.has('recommendations') && !calc && (
                                        <td colSpan={recommendationsColCount} className={tableStyles.cell}>-</td>
                                    )}

                                    {/* New Nutrient Levels cells */}
                                    {visibleGroups.has('nutrientLevels') && calc && (
                                        <>
                                            {/* mg/kg Y1 */}
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newCaMgKg)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newMgMgKg)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newKMgKg)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newPMgKg)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newNaMgKg)}</td>
                                            {/* mg/kg Y2 */}
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newCaMgKgY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newMgMgKgY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newKMgKgY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newPMgKgY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatMgKg(calc.newNaMgKgY2)}</td>
                                            {/* % Sat Y1 */}
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterCa)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterMg)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterK)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterNa)}</td>
                                            {/* % Sat Y2 */}
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterCaY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterMgY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterKY2)}</td>
                                            <td className={tableStyles.cellRight}>{formatSatPercent(calc.satAfterNaY2)}</td>
                                        </>
                                    )}

                                    {/* Placeholder when nutrient levels visible but no calc data */}
                                    {visibleGroups.has('nutrientLevels') && !calc && (
                                        <td colSpan={nutrientLevelsColCount} className={tableStyles.cell}>-</td>
                                    )}

                                    {/* Actions column */}
                                    <td className={tableStyles.cell}>
                                        <span className="underline cursor-pointer text-primary" onClick={() => onRemove(idx)}>
                                            {app.word('remove')}
                                        </span>
                                        <span className="px-2">|</span>
                                        <span className="underline cursor-pointer text-primary" onClick={() => onEdit(row, idx)}>
                                            {app.word('edit')}
                                        </span>
                                    </td>
                                </tr>
                            )
                        })}
                    </tbody>
                </table>
            </div>
        )}

        <div className="mt-2">
            <span title={app.word('add')} className="ml-1">
                <PlusCircleIcon className="w-6 h-6 inline-block cursor-pointer" onClick={() => addRow()}/>
            </span>
            <span title={app.word('upload_csv')} className="ml-1">
                <ArrowUpOnSquareIcon className="w-6 h-6 inline-block cursor-pointer" onClick={() => setShowUpload(true)}/>
            </span>
            {(samplePoints.length > 0 || (props.landIds && props.landIds.length > 0)) && (
                <span title={app.word('view_map')} className="ml-1">
                    <MapIcon
                        className="w-6 h-6 inline-block cursor-pointer text-primary"
                        onClick={() => setShowMap(true)}
                    />
                </span>
            )}
        </div>
    </div>
}

export default React.memo(LimePlayfieldUnified)
